import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';

const STORAGE_PREFIX = 'POSTPONED_ONBOARDING_TASKS_';

/**
 * A teacher who has just met the welcome board and the survey does not need the
 * onboarding tasks as a third screen in the same breath. This takes them off the
 * session that would have shown them and writes down that they are still owed,
 * so the next visit opens them instead.
 *
 * Only the Educators queue postpones anything. Everywhere else the tasks open
 * exactly when they always did.
 */
const usePostponedOnboardingTasks = ({ isEducatorsQueueRunning }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.id);
  const showOnboardingTasks = useSelector(
    (state) => state.layout.showOnboardingTasks,
  );
  // The session that writes the note must not be the one that redeems it: the
  // queue ends when the survey is answered, which is still this session.
  const hasPostponedHere = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const storageKey = `${STORAGE_PREFIX}${userId}`;

    if (isEducatorsQueueRunning) {
      if (!showOnboardingTasks) return;

      hasPostponedHere.current = true;
      localStorage.setItem(storageKey, 'true');
      dispatch(actions.layout.setShowOnboardingTasks(false));
      return;
    }

    if (hasPostponedHere.current) return;
    if (!localStorage.getItem(storageKey)) return;

    localStorage.removeItem(storageKey);
    dispatch(actions.layout.setShowOnboardingTasks(true));
  }, [userId, isEducatorsQueueRunning, showOnboardingTasks, dispatch]);
};

export default usePostponedOnboardingTasks;
